import { service } from "@ember/service";
import type Store from "ember-data/store";
import Base from "ember-simple-auth/authenticators/base";
import { SessionRestoreError } from "parog-web/authenticators/utils/errors";
import { ExpiredJWTError, verifyJWT } from "parog-web/authenticators/utils/jwt";
import type NativeAppService from "parog-web/services/native-app";
import type NetworkDetectorService from "parog-web/services/network-detector";
import type SemeiaSessionService from "parog-web/services/session";
import ControlTower from "wise-control-tower";

export default class PatientImplicitWithATAuthenticator extends Base {
  @service declare nativeApp: NativeAppService;
  @service declare networkDetector: NetworkDetectorService;
  @service requestManager;
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  override async authenticate(requestData) {
    // If the patient is already authenticated, keep him logged in
    // with the previous access token if the new one is already expired

    // FIXME actually, this code will be called if a link with an access token has been opened.
    //   Here, we must check the identity contained in the token, and logout if different.
    //   Otherwise, use the token if it is valid, or ignore the authenticate and resolve otherwise
    //   (we keep the active session active).

    // TODO we should check for validity of stored token before finalizing login process.
    // This code is weird: an invalid-but-not-expired request token does not stop the
    // flow here — it is still sent to the backend, because the local session may be good.
    // An expired request token with no valid local session is short-circuited below.
    let requestJWTError: Error | undefined;

    try {
      verifyJWT(requestData.access_token);
    } catch (e) {
      requestJWTError = e as Error;
    }

    const isRequestJWTInvalid = requestJWTError !== undefined;

    if (
      this.session.isAuthenticated &&
      this.session.data.authenticated.as === "patient" &&
      isRequestJWTInvalid
    ) {
      try {
        verifyJWT(this.session.data.authenticated.access_token);
        this.verifyAuthenticatedData(this.session.data.authenticated);
      } catch (e) {
        throw new SessionRestoreError({ cause: e });
      }

      this.nativeApp.patientLoggedIn();
      const connectionInfo = this.networkDetector.detectConnection();

      await this.saveConnectionInfo(
        connectionInfo,
        "Patient",
        this.session.patient?.id,
        "NativeMobileApp"
      );
      return this.session.data.authenticated;
    }

    if (requestJWTError instanceof ExpiredJWTError) {
      throw new SessionRestoreError({ cause: requestJWTError });
    }

    let responseBody;

    try {
      const { content } = await this.requestManager.request({
        url: "/api/v1/authorize_token",
        method: "POST",
        body: JSON.stringify(requestData)
      });

      responseBody = content;
    } catch (error) {
      if (error && !error.response.ok) {
        throw error.content;
      }
      throw error;
    }

    const data = {
      as: "patient",
      id: responseBody.patient_id,
      uuid: responseBody.patient_uuid,
      health_centre_id: responseBody.patient_health_centre_id,
      health_centre_uuid: responseBody.patient_health_centre_uuid,
      access_token: responseBody.jwt
    };

    await ControlTower.setUser({ ...data, class: "Patient" });
    this.nativeApp.patientLoggedIn();

    this.session.isAuthenticatedAtThisSession = true;
    await this.session.fetchAuthenticatedUser(data);
    return data;
  }

  override async invalidate() {
    await ControlTower.resetUser();
  }

  override async restore(data) {
    try {
      verifyJWT(data.access_token);
      this.verifyAuthenticatedData(data);
    } catch (e) {
      await ControlTower.resetUser();
      throw new SessionRestoreError({ cause: e });
    }

    await ControlTower.setUser({ ...data, class: "Patient" });
    // FIXME misses patientLoggedIn?
    await this.session.fetchAuthenticatedUser(data);
    return data;
  }

  async saveConnectionInfo(connectionInfo, creatorType, creatorId, appType) {
    if (!connectionInfo) return;

    await this.store
      .createRecord("user-connection-journal-event", {
        occurredAt: new Date(),
        networkInformation: connectionInfo.effectiveType,
        creatorType: creatorType,
        creatorId: creatorId,
        appType: appType
      })
      .save();
  }

  private verifyAuthenticatedData(data: any) {
    if (!["patient", "medic"].includes(data.as)) {
      throw "Invalid data property: as";
    }

    if (!data.id) {
      throw "Missing data property: id";
    }
  }
}
